import { useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { SignIn } from "@clerk/clerk-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ShieldAlert, Ban } from "lucide-react";
import { useLocation } from "wouter";

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-white">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute -bottom-[20%] -right-[10%] w-[60%] h-[80%] opacity-[0.03]"
          style={{ transform: "rotate(23.5deg)" }}
        >
          <svg viewBox="0 0 100 120" className="w-full h-full">
            <text x="10" y="110" fontSize="140" fontWeight="bold" fill="rgb(90,103,113)" fontFamily="Tahoma, sans-serif">D</text>
          </svg>
        </div>
        <div className="absolute top-0 left-0 w-full h-1 bg-duffells-orange" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md p-8 bg-white rounded-3xl relative z-10 mx-4 shadow-xl border border-slate-100"
      >
        {children}
      </motion.div>
    </div>
  );
}

export default function Login() {
  const { accessStatus, logout, isLoggingOut } = useAuth();
  const [, setLocation] = useLocation();

  // Once fully authorized, leave the login screen.
  useEffect(() => {
    if (accessStatus === "ok") setLocation("/");
  }, [accessStatus, setLocation]);

  if (accessStatus === "loading") {
    return (
      <Shell>
        <div className="flex justify-center py-10">
          <div className="w-8 h-8 rounded-full border-4 border-primary border-t-transparent animate-spin" />
        </div>
      </Shell>
    );
  }

  if (accessStatus === "no_access" || accessStatus === "deactivated") {
    const isDeactivated = accessStatus === "deactivated";
    return (
      <Shell>
        <div className="flex flex-col items-center text-center">
          <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mb-4">
            {isDeactivated ? (
              <Ban className="w-7 h-7 text-red-500" />
            ) : (
              <ShieldAlert className="w-7 h-7 text-red-500" />
            )}
          </div>
          <h1 className="text-2xl font-display font-bold text-duffells-grey">
            {isDeactivated ? "Account deactivated" : "No access"}
          </h1>
          <p className="text-duffells-grey/80 mt-2">
            {isDeactivated
              ? "Your account has been deactivated. Please contact your administrator if you believe this is a mistake."
              : "You're signed in, but you don't have access to the Duffells Expenses app. Ask an administrator to grant you access."}
          </p>
          <Button
            variant="outline"
            className="mt-6"
            onClick={() => logout()}
            disabled={isLoggingOut}
          >
            {isLoggingOut ? "Signing out..." : "Sign out"}
          </Button>
        </div>
      </Shell>
    );
  }

  // signed_out — render the Clerk sign-in widget.
  return (
    <Shell>
      <div className="flex flex-col items-center mb-6">
        <div className="w-14 h-14 rounded-2xl bg-duffells-orange flex items-center justify-center shadow-lg shadow-primary/20 mb-4">
          <span className="text-white font-bold text-2xl">D</span>
        </div>
        <h1 className="text-3xl font-display font-bold text-duffells-orange">Welcome Back</h1>
        <p className="text-duffells-grey mt-2 text-center">
          Sign in to manage your corporate expenses and matching.
        </p>
      </div>
      <div className="flex justify-center">
        <SignIn routing="virtual" />
      </div>
    </Shell>
  );
}
